import {
  isDateLessThan5Years,
  isDateMoreThan5Years,
} from '@shared/fondof-fn/date/check'
import { toCamelCase } from '@shared/fondof-fn/format/string'
import { toProductDetailFeatures } from '@shared/shop-api/category/features/mapper'
import { toCategory } from '@shared/shop-api/category/mapper'
import { toSizeInformation } from '@shared/shop-api/category/size-information/mapper'
import { gidToId } from '@shared/shop-api/core/helper'
import {
  getMetafieldBoolean,
  getMetafieldJson,
  getMetafieldString,
} from '@shared/shop-api/metafield/mapper'
import { toProductAttributes } from '@shared/shop-api/product/attributes/mapper'
import type {
  Media,
  MetafieldItem,
  ProductWithVariants,
} from '@shared/shop-api/product/types'
import type { ProductFragment } from '@shared/types/storefront/graphql'
import { isAfter } from 'date-fns'

import { trackError } from '@fond-of/telemetry'

const PRODUCT_DETAIL_FEATURE = {
  sizeInfoPdf: 'pdf',
  sizeInfoModal: 'modal',
  sizeFinder: 'sizefinder',
} as const

const getImagePerspective = (imageUrl: string): string | undefined => {
  // Extract the perspective identifier from image URLs that follow this pattern
  const match = imageUrl.match(
    /\/([^/]+?)(?:_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})?\.(?:png|jpg|gif)/
  )
  return match ? match[1] : undefined
}

const toProductDetailFeatureSet = (product: ProductFragment): Set<string> => {
  return new Set(
    product.collections.items.flatMap((collection) =>
      toProductDetailFeatures(collection?.productDetailFeatures)
    )
  )
}

const hasProductDetailFeature = (
  productDetailFeatureSet: Set<string>,
  featureHandle: string
) => {
  return productDetailFeatureSet.has(featureHandle)
}

export const toProduct = (product: ProductFragment) => {
  const id = gidToId(product.id)
  const attributes = toProductAttributes(product.metafields)
  const productDetailFeatureSet = toProductDetailFeatureSet(product)
  const isShoeProduct = attributes.mainCategory?.toUpperCase() === 'FOOTWEAR'

  const isComingSoonRestock =
    isDateLessThan5Years(attributes.restockDate) && !product.availableForSale

  const isComingSoonLaunch = isDateLessThan5Years(attributes.launchDate)

  const isComingSoon =
    isDateMoreThan5Years(attributes.restockDate) ||
    isDateMoreThan5Years(attributes.launchDate)

  const videos = ((): MetafieldItem[] | null => {
    const video = product.metafields.find(
      (field) => field?.key === 'videos'
    )?.value

    if (!video) return null

    try {
      return JSON.parse(video)
    } catch (error) {
      trackError(error)
      return null
    }
  })()

  const media = {
    ...product.images.items.reduce(
      (acc, image) => {
        if (image.id && image.url) {
          const perspective = getImagePerspective(image.url)
          const imageId = perspective ? toCamelCase(perspective) : image.id

          acc[imageId as keyof typeof acc] = {
            mediaType: 'image',
            url: image.url,
            altText: image.altText ?? undefined,
            thumbhash: image.thumbhash ?? undefined,
          }
        }
        return acc
      },
      {} as Record<string, Media>
    ),
    ...videos?.reduce(
      (acc, video: MetafieldItem) => {
        acc[video.key] = {
          mediaType: 'video',
          url: video.url,
        }
        return acc
      },
      {} as Record<string, Media>
    ),
  }

  if (product.featuredImage) {
    media.default = {
      mediaType: 'image',
      url: product.featuredImage.url,
      thumbhash: product.featuredImage.thumbhash ?? undefined,
      altText: product.featuredImage.altText ?? undefined,
    }
  }

  const categories = product.collections.items.map(toCategory)

  const userManualIds = categories
    .map((category) => category.userManualId)
    .filter(Boolean) as string[]

  const sizeInformation = product.collections.items.reduce<
    ReturnType<typeof toSizeInformation>
  >(
    (found, collection) => {
      if (found.contentfulId || found.pdfUrl) return found
      return toSizeInformation(collection.sizeInformation)
    },
    { contentfulId: undefined, pdfUrl: undefined }
  )

  const sizeInfoContentId = sizeInformation.contentfulId
  const sizeInfoPdfUrl = sizeInformation.pdfUrl

  const productModelDetails = categories.flatMap(
    (category) => category.productModelDetails ?? []
  )

  return {
    id,
    name: product.title,
    hasBattery: Boolean(attributes.batteryUnNumber),
    isCaseable: Boolean(attributes.caseableSku),
    available: product.availableForSale,
    attributes,
    url: `/p/${product.handle}`,
    hreflang: getMetafieldJson(product.metafields, 'hreflang'),
    canonical: getMetafieldString(product.metafields, 'canonical'),
    media,
    showEdition:
      !checkProductDate(attributes.launchDate) &&
      !checkProductDate(attributes.restockDate),
    description: product.descriptionHtml,
    isComingSoonRestock,
    isComingSoonLaunch,
    isComingSoon,
    restockDate: attributes.restockDate,
    launchDate: attributes.launchDate,
    isNew: attributes.isNew,
    handle: product.handle,
    metaDescription: product.seo?.description ?? undefined,
    metaTitle: product.seo?.title ?? undefined,
    seoName: attributes.name ?? undefined,
    shortDescription: attributes.shortDescription ?? undefined,
    categories,
    showSizeInfoPdf: hasProductDetailFeature(
      productDetailFeatureSet,
      PRODUCT_DETAIL_FEATURE.sizeInfoPdf
    ),
    showSizeInfoModal: hasProductDetailFeature(
      productDetailFeatureSet,
      PRODUCT_DETAIL_FEATURE.sizeInfoModal
    ),
    showSizeFinder: hasProductDetailFeature(
      productDetailFeatureSet,
      PRODUCT_DETAIL_FEATURE.sizeFinder
    ),
    isShoe: isShoeProduct,
    sizeInfoContentId,
    sizeInfoPdfUrl,
    userManualIds,
    productModelDetails,
    robots: `${getMetafieldBoolean(product.metafields, 'no_index', false) ? 'noindex' : 'index'}, follow`,
    /**
     * @deprecated removed without replacement
     */
    isVoucher: false,
    /**
     * @deprecated removed without replacement
     */
    isDigital: false,
    /**
     * @deprecated use `id` instead
     */
    abstractId: id,
    /**
     * @deprecated use `name` instead
     */
    abstractName: product.title,
    /**
     * @deprecated use the currency of the product variant instead
     */
    currency: {
      code: 'EUR',
      symbol: '€',
      name: 'Euro',
    },
  }
}

const checkProductDate = (
  productDate?: Date | number | string | null
): boolean => {
  if (!productDate) {
    return false
  }

  const date = new Date(productDate)
  const now = new Date()

  return date && isAfter(date, now) && !isDateMoreThan5Years(date)
}

export const toListProduct = (item: ProductWithVariants) => {
  const [variant] = item.variants

  return {
    sku: variant.sku,
    brand: item.attributes.brand,
    price: variant.price,
    modelUntranslated: String(item.attributes.modelUntranslated),
    styleUntranslated: String(item.attributes.styleUntranslated),
    size: variant.size ?? '',
  }
}
